import { useEffect, useRef } from 'react';

import { AUTH_ACCENT_HUE } from './auth-config';

interface FlowBackgroundProps {
    /** Accent hue (OKLCH, 0–360). Defaults to {@link AUTH_ACCENT_HUE}. */
    accentHue?: number;
    /** Render travelling particles along each centerline. */
    showParticles?: boolean;
    /** Current colour scheme — drives canvas gradient / grid colours. */
    resolvedTheme: 'light' | 'dark';
}

interface Curve {
    i: number;
    phase: number;
    speed: number;
    baseY: number;
    amp: number;
    thickness: number;
    opacity: number;
    particles: { t: number; speed: number }[];
}

const seedRand = (s: number) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
};

/**
 * Animated centerline-inspired canvas background — vessel-like curves
 * drifting across the panel with mouse-attracted particles.
 *
 * Pure canvas drawing, no React state per-frame. The component
 * re-mounts the draw loop when `accentHue` or `resolvedTheme` change.
 */
export function FlowBackground({
    accentHue = AUTH_ACCENT_HUE,
    showParticles = true,
    resolvedTheme,
}: FlowBackgroundProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouseRef = useRef({ x: 0.5, y: 0.5, tx: 0.5, ty: 0.5, active: false });
    const rafRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let w = 0;
        let h = 0;

        // Use ResizeObserver so we pick up the canvas's real size even
        // when the parent's flex layout settles after the effect runs.
        // Falling back to getBoundingClientRect on mount can return 0×0
        // and freeze the animation on a zero-sized buffer.
        const applySize = (width: number, height: number) => {
            if (width === w && height === h) return;
            w = width;
            h = height;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        const rect = canvas.getBoundingClientRect();
        applySize(rect.width, rect.height);
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const { width, height } = entry.contentRect;
            applySize(width, height);
        });
        ro.observe(canvas);

        const onMove = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            mouseRef.current.tx = (e.clientX - rect.left) / rect.width;
            mouseRef.current.ty = (e.clientY - rect.top) / rect.height;
            mouseRef.current.active = true;
        };
        const onLeave = () => {
            mouseRef.current.active = false;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseleave', onLeave);

        // Pre-compute curves once per mount.
        const curves: Curve[] = Array.from({ length: 7 }, (_, i) => ({
            i,
            phase: i * 0.9,
            speed: 0.06 + seedRand(i + 1) * 0.05,
            baseY: 0.15 + (i / 6) * 0.7,
            amp: 0.06 + seedRand(i + 11) * 0.05,
            thickness: 0.6 + seedRand(i + 21) * 1.2,
            opacity: 0.18 + seedRand(i + 31) * 0.25,
            particles: Array.from({ length: 3 }, (_, k) => ({
                t: seedRand(i * 7 + k),
                speed: 0.03 + seedRand(i * 11 + k) * 0.04,
            })),
        }));

        // Theme-dependent palette. We pre-resolve all colour strings so
        // the draw loop just plugs them in.
        const isDark = resolvedTheme === 'dark';
        const palette = {
            glowInner: isDark
                ? `oklch(40% 0.12 ${accentHue} / 0.35)`
                : `oklch(95% 0.06 ${accentHue} / 0.55)`,
            glowMid: isDark
                ? `oklch(30% 0.06 ${accentHue} / 0.22)`
                : `oklch(96% 0.03 ${accentHue} / 0.30)`,
            glowOuter: isDark ? 'rgba(15, 23, 35, 0)' : 'rgba(255, 255, 255, 0)',
            gridDot: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 35, 0.06)',
            stroke: (alpha: number) =>
                isDark
                    ? `oklch(70% 0.16 ${accentHue} / ${alpha})`
                    : `oklch(55% 0.16 ${accentHue} / ${alpha})`,
            particleCore: isDark
                ? `oklch(75% 0.18 ${accentHue})`
                : `oklch(55% 0.18 ${accentHue})`,
            particleGlowInner: isDark
                ? `oklch(80% 0.18 ${accentHue} / 0.55)`
                : `oklch(70% 0.18 ${accentHue} / 0.55)`,
            particleGlowOuter: `oklch(70% 0.18 ${accentHue} / 0)`,
        };

        let t0 = performance.now();
        const draw = (now: number) => {
            // Bail until ResizeObserver has reported real dimensions —
            // dividing by w/h in the curve loop otherwise produces NaN
            // coordinates that propagate into particle `t` values and
            // crash on the next `points[i0]` lookup.
            if (w === 0 || h === 0) {
                t0 = now;
                rafRef.current = requestAnimationFrame(draw);
                return;
            }

            const rawDt = (now - t0) / 1000;
            const dt = Number.isFinite(rawDt) ? Math.min(0.05, Math.max(0, rawDt)) : 0;
            t0 = now;
            const t = now / 1000;

            // Mouse smoothing.
            const m = mouseRef.current;
            m.x += (m.tx - m.x) * 0.08;
            m.y += (m.ty - m.y) * 0.08;

            ctx.clearRect(0, 0, w, h);

            // Radial glow following the cursor.
            const g = ctx.createRadialGradient(
                m.x * w,
                m.y * h,
                0,
                m.x * w,
                m.y * h,
                Math.max(w, h) * 0.85,
            );
            g.addColorStop(0, palette.glowInner);
            g.addColorStop(0.4, palette.glowMid);
            g.addColorStop(1, palette.glowOuter);
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);

            // Subtle dotted grid.
            ctx.fillStyle = palette.gridDot;
            const gridStep = 32;
            for (let x = gridStep; x < w; x += gridStep) {
                for (let y = gridStep; y < h; y += gridStep) {
                    ctx.fillRect(x, y, 1, 1);
                }
            }

            // Curves.
            for (const c of curves) {
                const N = 60;
                const points: [number, number][] = [];
                for (let k = 0; k <= N; k++) {
                    const u = k / N;
                    const x = u * w;
                    let y = (c.baseY + Math.sin(t * c.speed + c.phase + u * 6.0) * c.amp) * h;
                    y += Math.sin(t * 0.4 + u * 12 + c.i) * 8;
                    // Soft mouse attraction.
                    const dx = x / w - m.x;
                    const dy = y / h - m.y;
                    const d2 = dx * dx + dy * dy;
                    const pull = Math.exp(-d2 * 12);
                    y += (m.y * h - y) * pull * 0.35;
                    points.push([x, y]);
                }

                ctx.strokeStyle = palette.stroke(c.opacity * 1.1);
                ctx.lineWidth = c.thickness;
                ctx.beginPath();
                ctx.moveTo(points[0][0], points[0][1]);
                for (let k = 1; k < points.length - 1; k++) {
                    const [x1, y1] = points[k];
                    const [x2, y2] = points[k + 1];
                    const xc = (x1 + x2) / 2;
                    const yc = (y1 + y2) / 2;
                    ctx.quadraticCurveTo(x1, y1, xc, yc);
                }
                const last = points[points.length - 1];
                ctx.lineTo(last[0], last[1]);
                ctx.stroke();

                if (showParticles) {
                    for (const p of c.particles) {
                        // Advance and wrap into [0, 1). Self-heal if a
                        // prior frame (or a stale RAF) left `p.t` as
                        // NaN — otherwise `idx → NaN → i0 = NaN` would
                        // make `points[i0]` undefined and crash below.
                        p.t += p.speed * dt;
                        if (!Number.isFinite(p.t)) p.t = 0;
                        p.t -= Math.floor(p.t);
                        const lastIdx = points.length - 1;
                        const idx = p.t * lastIdx;
                        const i0 = Math.min(Math.max(Math.floor(idx), 0), lastIdx);
                        const i1 = Math.min(i0 + 1, lastIdx);
                        const a = points[i0];
                        const b = points[i1];
                        if (!a || !b) continue;
                        const f = idx - i0;
                        const px = a[0] + (b[0] - a[0]) * f;
                        const py = a[1] + (b[1] - a[1]) * f;
                        const r = 2.4;
                        const gg = ctx.createRadialGradient(px, py, 0, px, py, r * 6);
                        gg.addColorStop(0, palette.particleGlowInner);
                        gg.addColorStop(1, palette.particleGlowOuter);
                        ctx.fillStyle = gg;
                        ctx.beginPath();
                        ctx.arc(px, py, r * 6, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.fillStyle = palette.particleCore;
                        ctx.beginPath();
                        ctx.arc(px, py, r, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }

            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(rafRef.current);
            ro.disconnect();
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseleave', onLeave);
        };
    }, [accentHue, resolvedTheme, showParticles]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 block h-full w-full"
        />
    );
}
